import { lazy } from 'react';

/**
 * A wrapper around React.lazy() that shows update notification when a chunk fails to load.
 * This commonly happens when a new version is deployed and old chunks are deleted.
 * 
 * @param {Function} componentImport - The dynamic import function, e.g., () => import('./Component')
 * @returns {React.Component} A lazy-loaded component
 */
export const lazyWithRetry = (componentImport) => {
    return lazy(async () => {
        try {
            const component = await componentImport();
            // Clear reload flag on successful load to allow future reloads if needed
            window.sessionStorage.removeItem('retry-lazy-refreshed');
            return component;
        } catch (error) {
            // Check if we already refreshed the page to avoid infinite loops
            const hasRefreshed = window.sessionStorage.getItem('retry-lazy-refreshed');

            // Check if error is related to chunk loading (version mismatch or connectivity)
            const isChunkLoadError =
                error.name === 'ChunkLoadError' ||
                error.message.includes('Loading chunk') ||
                error.message.includes('Loading CSS chunk') ||
                error.message.includes('Failed to fetch dynamically imported module') ||
                error.message.includes('Importing a module script failed') ||
                error.message.includes('Load failed');

            if (!hasRefreshed && isChunkLoadError) {
                // Set flag so we don't show notification again immediately
                window.sessionStorage.setItem('retry-lazy-refreshed', 'true');

                // Trigger the version checker's update notification
                // This gives users a better experience than the "Something went wrong" popup
                if (window.versionChecker) {
                    window.versionChecker.checkForUpdates();
                }

                // Return a never-resolving promise to keep the Suspense fallback visible
                return new Promise(() => { });
            }

            // If we already refreshed or it's a different error, bubble it up to ErrorBoundary
            throw error;
        }
    });
};
